import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSidebar } from '../context/SidebarContext';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../../context/auth';
import { useProfileStore } from '../../../stores/profileStore';
import { useNotificationStore } from '../../../stores/notificationStore';
import { AdminNavigation, NOTIFICATION_REFRESH_INTERVAL } from '../../navigation/AdminNavigation';
import { AdminMobileNavigation } from '../../navigation/AdminMobileNavigation';
import LanguageSwitcher from '../../LanguageSwitcher';

const AppHeader: React.FC = () => {
  const [isApplicationMenuOpen, setApplicationMenuOpen] = useState(false);

  const { isMobileOpen, toggleSidebar, toggleMobileSidebar } = useSidebar();

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const { signOut } = useAuth();

  const { profile } = useProfileStore();
  const navigate = useNavigate();

  const handleToggle = () => {
    if (window.innerWidth >= 1024) {
      toggleSidebar();
    } else {
      toggleMobileSidebar();
    }
  };

  const handleSignOut = () => {
    signOut();
    navigate('/');
  };

  const toggleApplicationMenu = () => {
    setApplicationMenuOpen(!isApplicationMenuOpen);
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const { unreadCount, fetchUnreadCount } = useNotificationStore();

  useEffect(() => {
    if (profile) {
      fetchUnreadCount();
      const interval = setInterval(() => {
        fetchUnreadCount();
      }, NOTIFICATION_REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [profile, fetchUnreadCount]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      <header className="sticky top-0 flex w-full bg-white border-gray-300 z-30 rounded-b-lg shadow-sm lg:border-b">
        <div className="flex flex-col items-center justify-between grow lg:flex-row lg:px-6">
          <div className="flex items-center justify-between w-full gap-2 px-3 py-3 border-b border-gray-300 sm:gap-4 lg:justify-normal lg:border-b-0 lg:px-0 lg:py-4">
            <button
              className="items-center justify-center w-10 h-10 text-gray-500 border-gray-300 rounded-lg z-99999 lg:flex lg:h-11 lg:w-11 lg:border"
              onClick={handleToggle}
              aria-label="Toggle Sidebar"
            >
              {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <Link to="/" className="lg:hidden">
              <img className="dark:hidden w-6 h-8" src="/android-chrome-192x192.png" alt="Logo" />
              <img className="hidden dark:block" src="/android-chrome-192x192.png" alt="Logo" />
            </Link>

            <div className="lg:hidden">
              <LanguageSwitcher />
            </div>

            <div
              onClick={toggleApplicationMenu}
              className="flex items-center justify-center w-10 h-10 text-gray-700 rounded-lg z-99999 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 lg:hidden"
            >
              <AdminMobileNavigation
                isOpen={isMobileNavOpen}
                onToggle={() => setIsMobileNavOpen(!isMobileNavOpen)}
                onSignOut={() => handleSignOut()}
                notifications={unreadCount}
              />
            </div>
          </div>
          <div
            className={`${
              isApplicationMenuOpen ? 'flex' : 'hidden'
            } items-center justify-between w-full gap-4 px-5 py-4 lg:flex shadow-theme-md lg:justify-end lg:px-0 lg:shadow-none`}
          >
            <AdminNavigation />
          </div>
        </div>
      </header>
    </>
  );
};

export default AppHeader;
